import type { ID } from "@/lib/catalog/types";
import { api } from "@/lib/apiClient";
import { isAxiosError } from "axios";

export const EXPENSE_PAYMENT_METHODS = [
  { value: "cash", label: "Cash", channel: "Cash", referenceLabel: null },
  { value: "online", label: "Online Payment", channel: "Bank", referenceLabel: "Reference / Transaction #" },
  { value: "cheque", label: "Cheque", channel: "Bank", referenceLabel: "Cheque #" },
  { value: "pay_order", label: "Pay Order", channel: "Bank", referenceLabel: "Pay Order #" },
  { value: "cash_deposit", label: "Cash Deposit to Bank", channel: "Bank", referenceLabel: "Deposit Slip / Reference #" },
] as const;

export type ExpensePaymentMethod = (typeof EXPENSE_PAYMENT_METHODS)[number]["value"];

export function paymentMethodMeta(method: ExpensePaymentMethod | "" | null) {
  return EXPENSE_PAYMENT_METHODS.find((item) => item.value === method) ?? null;
}

export function paymentMethodUsesBank(method: ExpensePaymentMethod | "" | null): boolean {
  return paymentMethodMeta(method)?.channel === "Bank";
}

export function paymentMethodLabel(method: ExpensePaymentMethod | "" | null): string {
  return paymentMethodMeta(method)?.label ?? "Not specified";
}

export interface ExpenseRecord {
  id: string;
  expenseCategoryId: string;
  subcategoryName: string;
  categoryName: string;
  date: string; // yyyy-mm-dd
  amount: number;
  paymentMethod: ExpensePaymentMethod | "";
  paymentMethodLabel: string;
  bankId: string;
  bankName: string;
  referenceNo: string;
  chequeNo: string;
  chequeDate: string;
  description: string;
  createdAt: string;
  updatedAt: string;
}

export type ExpenseDraft = {
  expenseCategoryId: string;
  date: string;
  amount: number | string;
  paymentMethod: ExpensePaymentMethod | "";
  bankId: string;
  referenceNo: string;
  chequeNo: string;
  chequeDate: string;
  description: string;
};

const LS_KEY = "inventory.expenses.v1";

type ExpenseApiRecord = {
  id?: number | string;
  expense_category_id: number | string;
  category?: {
    id?: number | string;
    name?: string;
    account_detail_type_id?: number | string | null;
    account_detail_type_title?: string | null;
  } | null;
  date: string;
  amount: number | string;
  payment_method?: string | null;
  payment_method_label?: string | null;
  account_id?: number | string | null;
  bank_id?: number | string | null;
  bank?: { id?: number | string; name?: string } | null;
  reference_no?: string | null;
  cheque_no?: string | null;
  cheque_date?: string | null;
  description?: string | null;
  created_at?: string | null;
  updated_at?: string | null;
};

type UpsertExpenseApiResponse = {
  message?: string;
  data?: ExpenseApiRecord;
};

type DeleteExpenseApiResponse = {
  message?: string;
};

type UpsertExpenseResult = {
  record: ExpenseRecord;
  message: string;
};

type DeleteExpenseResult = {
  success: boolean;
  message: string;
};

function dedupeById(items: ExpenseRecord[]): ExpenseRecord[] {
  const byId = new Map<ID, ExpenseRecord>();
  for (const item of items) byId.set(item.id, item);
  return Array.from(byId.values());
}

function load(): ExpenseRecord[] {
  try {
    const raw = localStorage.getItem(LS_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw) as ExpenseRecord[];
    return Array.isArray(parsed)
      ? dedupeById(
          parsed.map((item) => ({
            ...item,
            subcategoryName: item.subcategoryName || item.categoryName || "",
            categoryName: item.categoryName || "",
            bankId: item.bankId ?? "",
            bankName: item.bankName ?? "",
            referenceNo: item.referenceNo ?? "",
            chequeNo: item.chequeNo ?? "",
            chequeDate: item.chequeDate ?? "",
            paymentMethod: mapPaymentMethod(item.paymentMethod),
            paymentMethodLabel: item.paymentMethodLabel || paymentMethodLabel(mapPaymentMethod(item.paymentMethod)),
          })),
        )
      : [];
  } catch {
    return [];
  }
}

function save(items: ExpenseRecord[]) {
  localStorage.setItem(LS_KEY, JSON.stringify(dedupeById(items)));
}

function extractRowsFromResponse(body: unknown): ExpenseApiRecord[] {
  if (Array.isArray(body)) return body as ExpenseApiRecord[];
  if (!body || typeof body !== "object") return [];
  const root = body as Record<string, unknown>;
  const data = root.data;
  if (Array.isArray(data)) return data as ExpenseApiRecord[];
  if (data && typeof data === "object" && !Array.isArray(data)) {
    const inner = (data as Record<string, unknown>).data;
    if (Array.isArray(inner)) return inner as ExpenseApiRecord[];
  }
  return [];
}

function readLastPageFromListBody(body: unknown): number | null {
  if (!body || typeof body !== "object") return null;
  const meta = (body as Record<string, unknown>).meta;
  if (meta && typeof meta === "object" && !Array.isArray(meta)) {
    const lp = (meta as Record<string, unknown>).last_page;
    if (typeof lp === "number" && lp >= 1) return lp;
  }
  return null;
}

function extractExpenseFromUpsertBody(body: unknown): ExpenseApiRecord | null {
  if (!body || typeof body !== "object") return null;
  const root = body as Record<string, unknown>;
  const data = root.data;
  if (data && typeof data === "object" && !Array.isArray(data)) return data as ExpenseApiRecord;
  return null;
}

function mapPaymentMethod(v: unknown): ExpensePaymentMethod | "" {
  if (v === "bank") return "online";
  if (v === "cash" || v === "online" || v === "cheque" || v === "pay_order" || v === "cash_deposit") return v;
  return "";
}

/** API may return `Y-m-d`, `Y-m-d H:i:s`, or ISO datetime; keep calendar + inputs as strict yyyy-MM-dd. */
export function normalizeExpenseDate(raw: unknown): string {
  if (raw == null) return "";
  const s = String(raw).trim();
  if (!s) return "";
  const m = s.match(/^(\d{4}-\d{2}-\d{2})/);
  return m ? m[1] : "";
}

function mapApiRecord(record: ExpenseApiRecord): ExpenseRecord {
  const created = record.created_at || new Date().toISOString();
  const updated = record.updated_at || created;
  const subcategoryName = record.category?.name || "";
  const categoryName = record.category?.account_detail_type_title || "";
  const amount = typeof record.amount === "string" ? Number(record.amount) : Number(record.amount || 0);

  return {
    id: String(record.id ?? ""),
    expenseCategoryId: String(record.expense_category_id ?? ""),
    subcategoryName,
    categoryName,
    date: normalizeExpenseDate(record.date),
    amount: Number.isFinite(amount) ? amount : 0,
    paymentMethod: mapPaymentMethod(record.payment_method),
    paymentMethodLabel: record.payment_method_label || paymentMethodLabel(mapPaymentMethod(record.payment_method)),
    bankId: record.bank_id != null && record.bank_id !== "" ? String(record.bank_id) : "",
    bankName: record.bank?.name ?? "",
    referenceNo: record.reference_no ?? "",
    chequeNo: record.cheque_no ?? "",
    chequeDate: normalizeExpenseDate(record.cheque_date),
    description: (record.description ?? "").toString(),
    createdAt: created,
    updatedAt: updated,
  };
}

export function listExpenses(): ExpenseRecord[] {
  return load().sort((a, b) => b.createdAt.localeCompare(a.createdAt));
}

const PER_PAGE = 50;

export async function syncExpensesFromApi() {
  try {
    const collected: ExpenseApiRecord[] = [];
    let page = 1;
    let lastPage = 1;
    do {
      const response = await api.get("/api/v1/expenses", {
        params: { page, per_page: PER_PAGE },
      });
      const body = response.data;
      collected.push(...extractRowsFromResponse(body));
      lastPage = readLastPageFromListBody(body) ?? 1;
      page += 1;
    } while (page <= lastPage && page <= 50);

    const records = dedupeById(collected.map(mapApiRecord).filter((r) => Boolean(r.id)));
    save(records);
    return listExpenses();
  } catch (error) {
    if (isAxiosError(error) && error.response?.status === 403) {
      throw error;
    }
    console.error("[Expenses] sync failed", error);
    return listExpenses();
  }
}

function buildApiPayload(draft: ExpenseDraft) {
  const description = draft.description.trim();
  const paymentMethod = draft.paymentMethod || null;
  const usesBank = paymentMethodUsesBank(paymentMethod);
  return {
    expense_category_id: Number(draft.expenseCategoryId) || draft.expenseCategoryId,
    date: normalizeExpenseDate(draft.date),
    amount: Number(draft.amount) || 0,
    payment_method: paymentMethod,
    bank_id: usesBank && draft.bankId ? Number(draft.bankId) : null,
    reference_no: paymentMethod === "online" || paymentMethod === "pay_order" || paymentMethod === "cash_deposit"
      ? draft.referenceNo.trim() || null
      : null,
    cheque_no: paymentMethod === "cheque" ? draft.chequeNo.trim() || null : null,
    cheque_date: paymentMethod === "cheque" ? normalizeExpenseDate(draft.chequeDate) || null : null,
    description: description || null,
  };
}

export async function createExpense(draft: ExpenseDraft): Promise<UpsertExpenseResult> {
  const items = load();
  const response = await api.post<UpsertExpenseApiResponse>("/api/v1/expenses", buildApiPayload(draft));
  const created = extractExpenseFromUpsertBody(response.data);
  if (!created || created.id == null) throw new Error("Invalid create expense response.");

  const record = mapApiRecord(created);
  const idx = items.findIndex((d) => d.id === record.id);
  if (idx >= 0) items[idx] = record;
  else items.unshift(record);
  save(items);
  return { record, message: response.data?.message || "Expense created successfully." };
}

export async function updateExpense(id: ID, draft: ExpenseDraft): Promise<UpsertExpenseResult | null> {
  const items = load();
  const idx = items.findIndex((d) => d.id === id);
  if (idx === -1) return null;

  const response = await api.put<UpsertExpenseApiResponse>(`/api/v1/expenses/${id}`, buildApiPayload(draft));
  const updated = extractExpenseFromUpsertBody(response.data);
  if (!updated) throw new Error("Invalid update expense response.");

  const record = mapApiRecord(updated);
  items[idx] = record;
  save(items);
  return { record, message: response.data?.message || "Expense updated successfully." };
}

export async function deleteExpense(id: ID): Promise<DeleteExpenseResult> {
  const response = await api.delete<DeleteExpenseApiResponse>(`/api/v1/expenses/${id}`);
  const items = load();
  const next = items.filter((d) => d.id !== id);
  const removed = next.length !== items.length;
  if (removed) save(next);
  return { success: removed, message: response.data?.message || "Expense deleted successfully." };
}

